import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ArrowRight, ShieldCheck, Heart, Brain, Clock, Sparkles } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} data-id="element-2">
    {children}
  </motion.div>;
const CHECKOUT_URL = 'https://puntobalance.com/cart/53267826409837:1';
const Button = ({
  children,
  href = CHECKOUT_URL,
  className = ''
}: {
  children: React.ReactNode;
  href?: string;
  className?: string;
}) => <a href={href} target={href.startsWith('#') ? undefined : '_blank'} rel={href.startsWith('#') ? undefined : 'noopener noreferrer'} onClick={e => {
  if (href.startsWith('#')) {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({
      behavior: 'smooth'
    });
  }
}} className={`inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-terracotta rounded-full hover:bg-terracotta-hover transition-colors duration-300 shadow-sm hover:shadow-md ${className}`} data-id="element-3">
    {children}
  </a>;
const legalContent = {
  privacy: {
    title: 'Política de Privacidad',
    sections: [{
      heading: 'Información que Recopilamos',
      text: 'Recopilamos información personal que nos proporcionas voluntariamente al registrarte en nuestro programa, como tu nombre y dirección de correo electrónico. No recopilamos información sensible ni compartimos tus datos con terceros.'
    }, {
      heading: 'Uso de la Información',
      text: 'Utilizamos tu información personal únicamente para: entregarte el contenido del programa, enviarte actualizaciones relacionadas con tu compra, y brindarte soporte si lo necesitas.'
    }, {
      heading: 'Protección de Datos',
      text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.'
    }, {
      heading: 'Cookies',
      text: 'Nuestro sitio web puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades.'
    }, {
      heading: 'Tus Derechos',
      text: 'Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes contactarnos directamente.'
    }]
  },
  terms: {
    title: 'Términos y Condiciones',
    sections: [{
      heading: 'Aceptación de los Términos',
      text: 'Al acceder y utilizar Calma Laboral 40+, aceptas estos términos y condiciones en su totalidad. Si no estás de acuerdo con alguna parte, te pedimos que no utilices el programa.'
    }, {
      heading: 'Descripción del Servicio',
      text: 'Calma Laboral 40+ es un programa digital de bienestar y manejo del estrés. No sustituye asesoría médica, psicológica o terapéutica profesional. Si necesitas atención clínica, consulta a un profesional de la salud.'
    }, {
      heading: 'Licencia de Uso',
      text: 'Al comprar el programa, obtienes una licencia personal e intransferible para acceder al contenido. No está permitido compartir, reproducir, distribuir o revender el material.'
    }, {
      heading: 'Política de Reembolso',
      text: 'Ofrecemos una garantía de devolución de 7 días desde la fecha de compra. Si dentro de ese período sientes que el programa no es para ti, puedes solicitar un reembolso completo sin preguntas.'
    }, {
      heading: 'Limitación de Responsabilidad',
      text: 'Los resultados pueden variar según cada persona. No garantizamos resultados específicos. El programa ofrece herramientas y estrategias, pero la aplicación y los resultados dependen de cada usuaria.'
    }, {
      heading: 'Modificaciones',
      text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios serán efectivos desde su publicación en el sitio web.'
    }]
  }
};
const LegalModal = ({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) => {
  const content = legalContent[type];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brown/60 backdrop-blur-sm" onClick={onClose} data-id="element-4">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="bg-cream rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-5">
        <div className="flex items-center justify-between p-6 border-b border-terracotta/10" data-id="element-6">
          <h2 className="text-2xl font-bold text-brown" data-id="element-7">{content.title}</h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center hover:bg-terracotta/20 transition-colors" data-id="element-8">
            <X className="w-5 h-5 text-terracotta" data-id="element-9" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)] space-y-8" data-id="element-10">
          {content.sections.map((section, i) => <div key={i} data-id="element-11">
              <h3 className="text-lg font-bold text-brown mb-2" data-id="element-12">
                {section.heading}
              </h3>
              <p className="text-brown-light leading-relaxed" data-id="element-13">{section.text}</p>
            </div>)}
          <p className="text-sm text-brown-light/60 pt-4 border-t border-terracotta/10" data-id="element-14">
            Última actualización: Abril 2026
          </p>
        </div>
      </motion.div>
    </motion.div>;
};
export function Home() {
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | null>(null);
  return <div className="min-h-screen bg-cream selection:bg-terracotta/20" data-id="element-15">
      {/* 1. Hero Section */}
      <section className="relative pt-20 pb-24 lg:pt-32 lg:pb-32 overflow-hidden" data-id="element-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8" data-id="element-17">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-18">
            <FadeIn data-id="element-19">
              <div className="max-w-2xl" data-id="element-20">
                <span className="inline-block py-1 px-3 rounded-full bg-terracotta/10 text-terracotta font-medium text-sm mb-6" data-id="element-21">
                  Programa Digital para Mujeres 40+
                </span>
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight mb-6 text-brown" data-id="element-22">
                  Reduce el estrés laboral, recupera claridad mental y vuelve a
                  sentir control en tu día.
                </h1>
                <p className="text-xl text-brown-light mb-8 leading-relaxed" data-id="element-23">
                  Si terminas tu jornada mentalmente agotada, irritable,
                  saturada y sintiendo que el trabajo se queda contigo incluso
                  después de cerrar la computadora, este programa es para ti.
                </p>
                <Button className="w-full sm:w-auto" data-id="element-24">
                  Quiero recuperar mi calma{' '}
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-25" />
                </Button>
              </div>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-26">
              <div className="relative" data-id="element-27">
                <div className="absolute inset-0 bg-terracotta/5 rounded-3xl transform rotate-3 scale-105 -z-10" data-id="element-28"></div>
                <img src="https://cdn.magicpatterns.com/uploads/kuFTe6etYJvEUwiLQU6X7W/Calma_laboral_402B.png" alt="Mujer tomando té relajada" className="rounded-3xl shadow-xl w-full object-cover" data-id="element-29" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 2. Empathy Section */}
      <section className="py-24 bg-cream-alt" data-id="element-30">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-31">
          <FadeIn data-id="element-32">
            <h2 className="text-3xl lg:text-5xl font-bold mb-12" data-id="element-33">
              No estás fallando.
              <br data-id="element-34" />
              Estás saturada.
            </h2>
            <div className="space-y-6 text-xl text-brown-light" data-id="element-35">
              <p data-id="element-36">No es que no seas capaz.</p>
              <p data-id="element-37">No es que te falte disciplina.</p>
              <p data-id="element-38">No es que tengas que esforzarte más.</p>
              <p className="font-medium text-brown mt-8" data-id="element-39">
                La realidad es que llevas demasiado encima.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-8" data-id="element-40">
                <span className="px-4 py-2 bg-white rounded-full shadow-sm text-terracotta" data-id="element-41">
                  Demasiadas tareas
                </span>
                <span className="px-4 py-2 bg-white rounded-full shadow-sm text-terracotta" data-id="element-42">
                  Demasiadas decisiones
                </span>
                <span className="px-4 py-2 bg-white rounded-full shadow-sm text-terracotta" data-id="element-43">
                  Demasiada presión
                </span>
                <span className="px-4 py-2 bg-white rounded-full shadow-sm text-terracotta" data-id="element-44">
                  Demasiada carga mental
                </span>
              </div>
              <p className="mt-12 text-2xl font-serif italic text-brown" data-id="element-45">
                "Y llega un punto en el que tu cuerpo sigue funcionando, pero tu
                mente ya está agotada."
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 3. Symptoms */}
      <section className="py-24 bg-cream" data-id="element-46">
        <div className="max-w-4xl mx-auto px-6" data-id="element-47">
          <FadeIn data-id="element-48">
            <h2 className="text-3xl lg:text-4xl font-bold text-center mb-16" data-id="element-49">
              Si esto te pasa, necesitas esto:
            </h2>
            <div className="grid sm:grid-cols-2 gap-6" data-id="element-50">
              {['Terminas el día drenada mentalmente', 'Te cuesta desconectar del trabajo', 'Sientes que todo es urgente', 'Estás irritable, cansada o emocionalmente saturada', 'Aunque sigues cumpliendo, por dentro te sientes al límite', 'Tienes demasiadas cosas en la cabeza al mismo tiempo', 'Quieres sentirte más tranquila, pero no sabes cómo bajar el ritmo sin dejar de responder'].map((item, i) => <div key={i} className="flex items-start p-6 bg-white rounded-2xl shadow-sm border border-terracotta/10" data-id="element-51">
                  <div className="flex-shrink-0 w-8 h-8 bg-terracotta/10 rounded-full flex items-center justify-center mr-4 mt-1" data-id="element-52">
                    <X className="w-5 h-5 text-terracotta" data-id="element-53" />
                  </div>
                  <p className="text-lg text-brown-light" data-id="element-54">{item}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 4. What is it */}
      <section className="py-24 bg-brown text-cream" data-id="element-55">
        <div className="max-w-4xl mx-auto px-6" data-id="element-56">
          <FadeIn data-id="element-57">
            <h2 className="text-3xl lg:text-5xl font-bold text-center mb-8 text-cream" data-id="element-58">
              ¿Qué es Calma Laboral 40+?
            </h2>
            <p className="text-xl text-center text-cream/90 mb-16 max-w-3xl mx-auto leading-relaxed" data-id="element-59">
              Es un programa digital práctico para mujeres 40+ que quieren dejar
              de vivir en modo urgencia y aprender una manera más humana, clara
              y realista de manejar el estrés laboral.
            </p>

            <div className="grid sm:grid-cols-3 gap-8 mb-16" data-id="element-60">
              <div className="text-center" data-id="element-61">
                <div className="w-16 h-16 mx-auto bg-cream/10 rounded-full flex items-center justify-center mb-4" data-id="element-62">
                  <X className="w-8 h-8 text-terracotta-light" data-id="element-63" />
                </div>
                <p className="text-lg" data-id="element-64">No necesitas desaparecer del trabajo.</p>
              </div>
              <div className="text-center" data-id="element-65">
                <div className="w-16 h-16 mx-auto bg-cream/10 rounded-full flex items-center justify-center mb-4" data-id="element-66">
                  <X className="w-8 h-8 text-terracotta-light" data-id="element-67" />
                </div>
                <p className="text-lg" data-id="element-68">No necesitas cambiar toda tu vida.</p>
              </div>
              <div className="text-center" data-id="element-69">
                <div className="w-16 h-16 mx-auto bg-cream/10 rounded-full flex items-center justify-center mb-4" data-id="element-70">
                  <X className="w-8 h-8 text-terracotta-light" data-id="element-71" />
                </div>
                <p className="text-lg" data-id="element-72">
                  No necesitas hacer rutinas imposibles.
                </p>
              </div>
            </div>

            <div className="bg-cream/5 rounded-3xl p-8 lg:p-12 border border-cream/10" data-id="element-73">
              <h3 className="text-2xl font-bold mb-8 text-center text-terracotta-light" data-id="element-74">
                Solo necesitas aprender un sistema simple para:
              </h3>
              <div className="grid sm:grid-cols-2 gap-6" data-id="element-75">
                {['Pausar el pico de estrés', 'Ordenar tu mente', 'Responder con más claridad', 'Cerrar el día sin seguir cargando todo por dentro'].map((item, i) => <div key={i} className="flex items-center space-x-4" data-id="element-76">
                    <Check className="w-6 h-6 text-terracotta-light flex-shrink-0" data-id="element-77" />
                    <span className="text-lg" data-id="element-78">{item}</span>
                  </div>)}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 5. Method */}
      <section className="py-24 bg-cream" data-id="element-79">
        <div className="max-w-5xl mx-auto px-6" data-id="element-80">
          <FadeIn data-id="element-81">
            <div className="text-center mb-16" data-id="element-82">
              <h2 className="text-3xl lg:text-5xl font-bold mb-6" data-id="element-83">
                El método: Pausa – Reordena – Responde
              </h2>
              <p className="text-xl text-brown-light max-w-2xl mx-auto" data-id="element-84">
                Un sistema de 3 pasos diseñado para la vida real, para que
                puedas aplicarlo incluso en tus días más ocupados.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-85">
              {[{
              step: '1',
              title: 'Pausa',
              desc: 'Aprendes a frenar el momento en el que el estrés empieza a subir, antes de explotar, bloquearte o reaccionar desde la saturación.',
              icon: <Heart className="w-8 h-8 text-terracotta" data-id="element-86" />
            }, {
              step: '2',
              title: 'Reordena',
              desc: 'Sacas ruido mental, organizas lo importante y dejas de sentir que todo pesa al mismo tiempo.',
              icon: <Brain className="w-8 h-8 text-terracotta" data-id="element-87" />
            }, {
              step: '3',
              title: 'Responde',
              desc: 'Tomas decisiones, trabajas y cierras tu día con más calma, más claridad y menos carga emocional.',
              icon: <Sparkles className="w-8 h-8 text-terracotta" data-id="element-88" />
            }].map((item, i) => <div key={i} className="bg-white p-8 rounded-3xl shadow-sm border border-terracotta/10 relative overflow-hidden group hover:shadow-md transition-shadow" data-id="element-89">
                  <div className="absolute -right-4 -top-4 text-9xl font-serif text-cream-alt font-bold z-0 group-hover:scale-110 transition-transform duration-500" data-id="element-90">
                    {item.step}
                  </div>
                  <div className="relative z-10" data-id="element-91">
                    <div className="w-16 h-16 bg-cream rounded-2xl flex items-center justify-center mb-6" data-id="element-92">
                      {item.icon}
                    </div>
                    <h3 className="text-2xl font-bold mb-4" data-id="element-93">{item.title}</h3>
                    <p className="text-brown-light leading-relaxed" data-id="element-94">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 6. Results */}
      <section className="py-24 bg-cream-alt" data-id="element-95">
        <div className="max-w-4xl mx-auto px-6" data-id="element-96">
          <FadeIn data-id="element-97">
            <h2 className="text-3xl lg:text-4xl font-bold text-center mb-12" data-id="element-98">
              Lo que vas a lograr
            </h2>
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-sm border border-terracotta/10" data-id="element-99">
              <p className="text-xl mb-8 text-center text-brown-light" data-id="element-100">
                Con Calma Laboral 40+, vas a poder:
              </p>
              <div className="grid sm:grid-cols-2 gap-y-6 gap-x-12" data-id="element-101">
                {['Sentirte menos saturada', 'Bajar la tensión mental', 'Recuperar foco y claridad', 'Identificar qué te está drenando', 'Cortar el pico de estrés más rápido', 'Ordenar pendientes sin tanta ansiedad', 'Dejar de llevarte el trabajo a casa', 'Terminar el día con más paz mental'].map((item, i) => <div key={i} className="flex items-center space-x-4" data-id="element-102">
                    <div className="w-6 h-6 rounded-full bg-sage/20 flex items-center justify-center flex-shrink-0" data-id="element-103">
                      <Check className="w-4 h-4 text-sage" data-id="element-104" />
                    </div>
                    <span className="text-lg font-medium text-brown" data-id="element-105">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 7. What's included */}
      <section className="py-24 bg-cream" data-id="element-106">
        <div className="max-w-5xl mx-auto px-6" data-id="element-107">
          <FadeIn data-id="element-108">
            <h2 className="text-3xl lg:text-5xl font-bold text-center mb-16" data-id="element-109">
              Qué incluye
            </h2>

            <div className="space-y-12" data-id="element-110">
              {/* Main Program */}
              <div data-id="element-111">
                <div className="inline-block bg-terracotta text-white px-4 py-1 rounded-full text-sm font-medium mb-6" data-id="element-112">
                  Programa Principal
                </div>
                <h3 className="text-3xl font-bold mb-8" data-id="element-113">Calma Laboral 40+</h3>
                <p className="text-xl text-brown-light mb-8" data-id="element-114">
                  Un sistema paso a paso dividido en 4 partes:
                </p>

                <div className="grid md:grid-cols-2 gap-6" data-id="element-115">
                  {[{
                  title: 'Módulo 1. Entiende tu estrés laboral',
                  desc: 'Descubre qué te está saturando realmente, identifica detonantes y entiende por qué te sientes así.'
                }, {
                  title: 'Módulo 2. Pausa',
                  desc: 'Aprende a bajar la tensión en momentos críticos con herramientas simples y rápidas.'
                }, {
                  title: 'Módulo 3. Reordena',
                  desc: 'Vacía tu mente, organiza tus prioridades y reduce el caos interno.'
                }, {
                  title: 'Módulo 4. Responde',
                  desc: 'Actúa con más claridad, baja la reactividad y aprende a cerrar tu jornada mejor.'
                }].map((mod, i) => <div key={i} className="bg-white p-6 rounded-2xl border-l-4 border-terracotta shadow-sm" data-id="element-116">
                      <h4 className="text-xl font-bold mb-2" data-id="element-117">{mod.title}</h4>
                      <p className="text-brown-light" data-id="element-118">{mod.desc}</p>
                    </div>)}
                </div>
              </div>

              {/* Extras */}
              <div className="pt-12 border-t border-terracotta/20" data-id="element-119">
                <h3 className="text-2xl font-bold mb-8" data-id="element-120">Además recibes:</h3>
                <div className="grid sm:grid-cols-2 gap-6" data-id="element-121">
                  {[{
                  title: 'Workbook práctico',
                  desc: 'Con ejercicios guiados, hojas de trabajo y espacios para aplicar el método a tu realidad.'
                }, {
                  title: 'Audios antiestrés',
                  desc: 'Para usar en momentos de presión, saturación o cansancio mental.'
                }, {
                  title: 'Checklist diaria antiestrés',
                  desc: 'Una guía simple para revisar tu día y bajar la carga.'
                }, {
                  title: 'Rutina de cierre laboral',
                  desc: 'Para ayudarte a desconectar mentalmente y no seguir en modo trabajo todo el tiempo.'
                }].map((item, i) => <div key={i} className="bg-cream-alt p-6 rounded-2xl" data-id="element-122">
                      <h4 className="text-lg font-bold mb-2 text-terracotta" data-id="element-123">
                        {item.title}
                      </h4>
                      <p className="text-brown-light" data-id="element-124">{item.desc}</p>
                    </div>)}
                </div>
              </div>

              {/* Bonuses */}
              <div className="pt-12 border-t border-terracotta/20" data-id="element-125">
                <div className="inline-block bg-brown text-cream px-4 py-1 rounded-full text-sm font-medium mb-6" data-id="element-126">
                  Bonus Exclusivos
                </div>
                <div className="grid md:grid-cols-3 gap-6" data-id="element-127">
                  {[{
                  title: "Audio 'Reset de 5 Minutos'",
                  desc: 'Para cuando sientas que ya no puedes más y necesites reiniciar rápido.'
                }, {
                  title: "Guía 'No Me Llevo el Trabajo a Casa'",
                  desc: 'Para ayudarte a cortar el ciclo de seguir pensando en trabajo todo el día.'
                }, {
                  title: 'Plan de 7 Días para Bajar la Sobrecarga Mental',
                  desc: 'Un plan simple para empezar a sentir alivio desde la primera semana.'
                }].map((bonus, i) => <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-terracotta/10 relative overflow-hidden" data-id="element-128">
                      <div className="absolute top-0 right-0 w-16 h-16 bg-terracotta/5 rounded-bl-full" data-id="element-129"></div>
                      <h4 className="text-lg font-bold mb-3 pr-8" data-id="element-130">
                        Bonus {i + 1}. {bonus.title}
                      </h4>
                      <p className="text-brown-light text-sm" data-id="element-131">{bonus.desc}</p>
                    </div>)}
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 8. Who is it for */}
      <section className="py-24 bg-brown text-cream" data-id="element-132">
        <div className="max-w-5xl mx-auto px-6" data-id="element-133">
          <FadeIn data-id="element-134">
            <div className="grid md:grid-cols-2 gap-12 lg:gap-24" data-id="element-135">
              <div data-id="element-136">
                <h3 className="text-3xl font-bold mb-8 text-terracotta-light" data-id="element-137">
                  Esto es para ti si...
                </h3>
                <ul className="space-y-4" data-id="element-138">
                  {['Eres mujer 40+', 'Trabajas y llevas mucha carga mental', 'Te sientes responsable de todo', 'Vives con cansancio emocional', 'Te cuesta desconectar', 'Quieres una solución práctica y no solo consejos vacíos', 'Necesitas sentir alivio real sin complicarte más'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-139">
                      <Check className="w-6 h-6 text-sage flex-shrink-0 mt-0.5" data-id="element-140" />
                      <span className="text-lg text-cream/90" data-id="element-141">{item}</span>
                    </li>)}
                </ul>
              </div>
              <div data-id="element-142">
                <h3 className="text-3xl font-bold mb-8 text-cream/50" data-id="element-143">
                  Esto NO es para ti si...
                </h3>
                <ul className="space-y-4" data-id="element-144">
                  {['Quieres una solución mágica sin aplicar nada', 'No estás dispuesta a hacer pequeños cambios diarios', 'Buscas un programa clínico o terapéutico profundo', 'No quieres revisar tus hábitos mentales y emocionales'].map((item, i) => <li key={i} className="flex items-start space-x-3 opacity-70" data-id="element-145">
                      <X className="w-6 h-6 flex-shrink-0 mt-0.5" data-id="element-146" />
                      <span className="text-lg" data-id="element-147">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 9. What makes it different */}
      <section className="py-24 bg-cream-alt" data-id="element-148">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-149">
          <FadeIn data-id="element-150">
            <h2 className="text-3xl lg:text-4xl font-bold mb-12" data-id="element-151">
              Lo que hace diferente a Calma Laboral 40+
            </h2>
            <div className="space-y-6 text-xl text-brown-light mb-12" data-id="element-152">
              <p data-id="element-153">Porque no te dice simplemente "organízate mejor".</p>
              <p data-id="element-154">Tampoco te llena de teoría.</p>
              <p data-id="element-155">Tampoco te exige rutinas imposibles.</p>
            </div>
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-terracotta/10" data-id="element-156">
              <p className="text-2xl font-serif font-medium text-terracotta mb-6" data-id="element-157">
                Este programa está pensado para la vida real.
              </p>
              <p className="text-lg text-brown-light leading-relaxed" data-id="element-158">
                Para mujeres que trabajan.
                <br data-id="element-159" />
                Que sostienen mucho.
                <br data-id="element-160" />
                Que están cansadas de vivir aceleradas.
                <br data-id="element-161" />Y que necesitan una herramienta clara, humana y aplicable.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 10. Visualization */}
      <section className="py-24 bg-cream" data-id="element-162">
        <div className="max-w-4xl mx-auto px-6" data-id="element-163">
          <FadeIn data-id="element-164">
            <div className="text-center mb-16" data-id="element-165">
              <h2 className="text-4xl lg:text-5xl font-serif italic text-terracotta mb-12" data-id="element-166">
                Imagina esto...
              </h2>
              <div className="space-y-6 text-2xl font-medium text-brown" data-id="element-167">
                <p data-id="element-168">Terminar tu día sin sentir que tu mente sigue corriendo.</p>
                <p data-id="element-169">
                  Tener herramientas para bajar el estrés cuando empieza a
                  subir.
                </p>
                <p data-id="element-170">Saber qué hacer cuando te sientes saturada.</p>
                <p data-id="element-171">Respirar con más calma.</p>
                <p data-id="element-172">Pensar con más claridad.</p>
                <p className="text-terracotta mt-8" data-id="element-173">
                  Y dejar de sentir que el trabajo se está comiendo tu paz
                  mental.
                </p>
              </div>
              <p className="mt-12 text-xl text-brown-light" data-id="element-174">
                Eso es lo que busca darte Calma Laboral 40+.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 11. Pricing & CTA */}
      <section id="pricing" className="py-24 bg-white border-y border-terracotta/10" data-id="element-175">
        <div className="max-w-3xl mx-auto px-6" data-id="element-176">
          <FadeIn data-id="element-177">
            <div className="bg-cream p-8 md:p-12 rounded-3xl shadow-lg border-2 border-terracotta/20 text-center relative overflow-hidden" data-id="element-178">
              <div className="absolute top-0 right-0 w-32 h-32 bg-terracotta/5 rounded-bl-full -z-10" data-id="element-179"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-terracotta/5 rounded-tr-full -z-10" data-id="element-180"></div>

              <h2 className="text-3xl font-bold mb-4" data-id="element-181">
                Hoy puedes acceder a Calma Laboral 40+ por:
              </h2>
              <div className="text-6xl md:text-8xl font-bold text-terracotta my-8 font-serif" data-id="element-182">
                $6.39
              </div>

              <div className="max-w-sm mx-auto text-left mb-10" data-id="element-183">
                <p className="font-bold mb-4 text-center" data-id="element-184">Incluye:</p>
                <ul className="space-y-3" data-id="element-185">
                  {['Programa completo', 'Workbook práctico', 'Audios antiestrés', 'Checklist diaria', 'Rutina de cierre laboral', 'Bonus 1: Audio Reset 5 Min', 'Bonus 2: Guía No Me Llevo el Trabajo', 'Bonus 3: Plan de 7 Días'].map((item, i) => <li key={i} className="flex items-center space-x-3" data-id="element-186">
                      <Check className="w-5 h-5 text-terracotta flex-shrink-0" data-id="element-187" />
                      <span className="text-brown-light" data-id="element-188">{item}</span>
                    </li>)}
                </ul>
              </div>

              <div className="space-y-6" data-id="element-189">
                <p className="text-xl font-medium" data-id="element-190">
                  Dale a tu mente un sistema, no más presión.
                </p>
                <Button className="w-full md:w-auto text-xl px-12 py-5 shadow-lg" data-id="element-191">
                  Haz clic y empieza hoy
                </Button>
                <div className="text-sm text-brown-light space-y-1" data-id="element-192">
                  <p data-id="element-193">Recupera claridad.</p>
                  <p data-id="element-194">Baja el estrés.</p>
                  <p data-id="element-195">Vuelve a sentir control en tu día.</p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 13. Guarantee */}
      <section className="py-16 bg-cream" data-id="element-196">
        <div className="max-w-2xl mx-auto px-6 text-center" data-id="element-197">
          <FadeIn data-id="element-198">
            <ShieldCheck className="w-12 h-12 text-terracotta mx-auto mb-6" data-id="element-199" />
            <h3 className="text-2xl font-bold mb-4" data-id="element-200">Garantía de 7 días</h3>
            <p className="text-brown-light" data-id="element-201">
              Prueba Calma Laboral 40+ y, si sientes que no es para ti, puedes
              solicitar tu devolución dentro de los primeros 7 días. Sin
              preguntas.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 14. Final Close */}
      <section className="py-24 bg-brown text-cream text-center" data-id="element-202">
        <div className="max-w-3xl mx-auto px-6" data-id="element-203">
          <FadeIn data-id="element-204">
            <h2 className="text-3xl lg:text-4xl font-bold mb-8 text-cream" data-id="element-205">
              No necesitas seguir viviendo en automático, saturada y al límite.
            </h2>
            <p className="text-xl text-cream/80 mb-12" data-id="element-206">
              Calma Laboral 40+ te da una forma práctica de sentirte mejor sin
              dejar de cumplir con tu vida real.
            </p>
            <Button className="bg-cream text-brown hover:bg-cream-alt" data-id="element-207">
              Quiero empezar ahora
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-brown border-t border-cream/10" data-id="element-208">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-209">
          <p className="text-cream/50 text-sm" data-id="element-210">
            © {new Date().getFullYear()} Calma Laboral 40+. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-211">
            <button onClick={() => setLegalModal('privacy')} className="text-cream/50 text-sm hover:text-cream transition-colors" data-id="element-212">
              Política de Privacidad
            </button>
            <button onClick={() => setLegalModal('terms')} className="text-cream/50 text-sm hover:text-cream transition-colors" data-id="element-213">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* Legal Modals */}
      <AnimatePresence data-id="element-214">
        {legalModal && <LegalModal type={legalModal} onClose={() => setLegalModal(null)} data-id="element-215" />}
      </AnimatePresence>
    </div>;
}